/**
 * CENTRAL API CONFIGURATION
 * =========================
 * 
 * ⚠️  SINGLE SOURCE OF TRUTH ⚠️
 * 
 * This is the ONLY place where API URLs should be defined.
 * To change the API endpoint, update DEFAULT_REMOTE_API_BASE below.
 * 
 * Current production endpoint: https://lpuw2q0rcl.execute-api.ap-south-1.amazonaws.com/dev
 * 
 * DO NOT:
 * - Create .env.local files to override this
 * - Add API URLs in other config files  
 * - Use multiple configuration sources
 * 
 * Just change the URL below and restart the dev server.
 */

type EnvValue = string | boolean | undefined
type EnvRecord = Record<string, EnvValue>

const runtimeEnv: EnvRecord = (() => {
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      return import.meta.env as EnvRecord
    }
  } catch (error) {
    console.warn('⚠️ Unable to read import.meta.env', error)
  }

  if (typeof process !== 'undefined' && process.env) {
    return process.env as EnvRecord
  }

  return {}
})()

const readEnv = (key: string): string | undefined => {
  const value = runtimeEnv?.[key]
  if (typeof value === 'string') {
    return value
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false'
  }
  return undefined
}

const normalizeFlag = (value?: string): boolean | null => {
  if (!value) {
    return null
  }
  const normalized = value.toLowerCase()
  if (['true', '1', 'yes', 'on', 'local'].includes(normalized)) {
    return true
  }
  if (['false', '0', 'no', 'off', 'remote'].includes(normalized)) {
    return false
  }
  return null
}

const envMode = readEnv('NODE_ENV') || readEnv('MODE') || readEnv('VITE_NODE_ENV')
const envEnvironment = readEnv('NEXT_PUBLIC_ENVIRONMENT') || readEnv('VITE_ENVIRONMENT')
const envDevFlag = normalizeFlag(readEnv('DEV'))
const envProdFlag = normalizeFlag(readEnv('PROD'))

const isDevelopment = envDevFlag ?? (envEnvironment?.toLowerCase() === 'development' || envMode === 'development')
const isProduction = envProdFlag ?? (envEnvironment?.toLowerCase() === 'production' || envMode === 'production')

// Debug environment variables
console.log('🔧 Environment Variables Debug:', {
  NODE_ENV: envMode,
  NEXT_PUBLIC_ENVIRONMENT: envEnvironment,
  NEXT_PUBLIC_USE_LOCAL_API: readEnv('NEXT_PUBLIC_USE_LOCAL_API') || readEnv('VITE_USE_LOCAL_API'),
  NEXT_PUBLIC_API_URL: readEnv('NEXT_PUBLIC_API_URL') || readEnv('VITE_API_URL'),
  NEXT_PUBLIC_REMOTE_API_URL: readEnv('NEXT_PUBLIC_REMOTE_API_URL') || readEnv('VITE_REMOTE_API_URL'),
  NEXT_PUBLIC_LOCAL_API_URL: readEnv('NEXT_PUBLIC_LOCAL_API_URL') || readEnv('VITE_LOCAL_API_URL'),
  NEXT_PUBLIC_API_VERSION: readEnv('NEXT_PUBLIC_API_VERSION') || readEnv('VITE_API_VERSION')
})


var DEFAULT_REMOTE_API_BASE_holder = 'https://lpuw2q0rcl.execute-api.ap-south-1.amazonaws.com/dev'  // NEW API Gateway
var DEFAULT_DEV_API_BASE_holder = 'https://lpuw2q0rcl.execute-api.ap-south-1.amazonaws.com/dev'    // NEW API Gateway  
var DEFAULT_LOCAL_API_BASE_holder = 'http://localhost:9000'
const DEFAULT_REMOTE_API_BASE = DEFAULT_REMOTE_API_BASE_holder        // Use new API Gateway
const DEFAULT_DEV_API_BASE = DEFAULT_DEV_API_BASE_holder        // Keep SAM for dev
const DEFAULT_LOCAL_API_BASE = DEFAULT_LOCAL_API_BASE_holder    // Use localhost for local


function normalizeBaseUrl(url?: string | null): string | null {
  if (!url) {
    return null
  }

  const trimmed = url.trim()
  if (!trimmed) {
    return null
  }

  // Remove trailing slashes to keep URL construction predictable
  return trimmed.replace(/\/+$/, '')
}

const envExplicitApiUrl = normalizeBaseUrl(readEnv('NEXT_PUBLIC_API_URL') || readEnv('VITE_API_URL'))
const envRemoteApiUrl = normalizeBaseUrl(readEnv('NEXT_PUBLIC_REMOTE_API_URL') || readEnv('VITE_REMOTE_API_URL'))
const envLocalApiUrl = normalizeBaseUrl(readEnv('NEXT_PUBLIC_LOCAL_API_URL') || readEnv('VITE_LOCAL_API_URL'))
const envUseLocalPreference = readEnv('NEXT_PUBLIC_USE_LOCAL_API') || readEnv('VITE_USE_LOCAL_API')
const resolvedUseLocalPreference = normalizeFlag(envUseLocalPreference)
const defaultShouldUseLocalApi = isDevelopment && !isProduction
const shouldUseLocalApi = resolvedUseLocalPreference ?? defaultShouldUseLocalApi
const resolvedApiVersion = readEnv('NEXT_PUBLIC_API_VERSION') || readEnv('VITE_API_VERSION') || 'v1'

// CENTRAL CONFIGURATION - ALL API URLS DEFINED HERE
export const API_CONFIG = {
  DEFAULT_REMOTE_API_BASE,
  DEFAULT_DEV_API_BASE,
  DEFAULT_LOCAL_API_BASE,
  // SAM-managed API Gateway (with CORS fixed) - production endpoint
  REMOTE_API_BASE: envRemoteApiUrl || DEFAULT_REMOTE_API_BASE,
  
  // Local development API - now also pointing to remote
  LOCAL_API_BASE: envLocalApiUrl || DEFAULT_LOCAL_API_BASE,
  
  // API version
  API_VERSION: resolvedApiVersion,
  
  // Use local or remote API based on environment variable - switch to remote API Gateway
  USE_LOCAL_API: shouldUseLocalApi
}

/**
 * Get the current API base URL
 * SIMPLIFIED: One place to change API endpoint
 */
export function getApiBaseUrl(): string {
  // SINGLE SOURCE OF TRUTH - Change this URL to switch API endpoints
  if (envExplicitApiUrl) {
    console.log('🔧 Using explicit API URL override:', envExplicitApiUrl)
    return envExplicitApiUrl
  }

  const useLocal = API_CONFIG.USE_LOCAL_API
  const currentApiUrl = useLocal ? API_CONFIG.LOCAL_API_BASE : API_CONFIG.REMOTE_API_BASE

  console.log('🔧 Using API URL:', currentApiUrl, useLocal ? '(localhost)' : '(API Gateway)')
  return currentApiUrl
}

/**
 * Build complete API URL
 * This is the ONLY function that should build API URLs
 */
export function buildApiUrl(endpoint: string): string {
  const baseUrl = getApiBaseUrl()
  
  // Clean endpoint
  let cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint
  
  // NO TRAILING SLASH LOGIC
  // FastAPI routes should not have trailing slashes added automatically
  // This was causing 307 redirects that broke the API prefix
  
  // Build URL with API version
  let fullUrl: string
  if (baseUrl.includes('/api/')) {
    // Base URL already includes /api/version path
    fullUrl = `${baseUrl}/${cleanEndpoint}`
  } else {
    // Base URL needs /api/version path
    fullUrl = `${baseUrl}/api/${API_CONFIG.API_VERSION}/${cleanEndpoint}`
  }
  
  console.log(`🔗 API URL: ${endpoint} → ${fullUrl}`)
  return fullUrl
}

/**
 * Build API URL for code execution - ALWAYS uses API Gateway (never localhost)
 * This ensures code execution always goes through the remote Lambda sandboxes
 */
export function buildCodeExecutionApiUrl(endpoint: string): string {
  // ALWAYS use remote API for code execution (Lambda only - including C++)
  const baseUrl = API_CONFIG.REMOTE_API_BASE
  
  // Clean endpoint
  let cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint
  
  // Build URL with API version
  let fullUrl: string
  if (baseUrl.includes('/api/')) {
    // Base URL already includes /api/version path
    fullUrl = `${baseUrl}/${cleanEndpoint}`
  } else {
    // Base URL needs /api/version path
    fullUrl = `${baseUrl}/api/${API_CONFIG.API_VERSION}/${cleanEndpoint}`
  }
  
  console.log(`🚀 CODE EXECUTION URL (ALWAYS Lambda API Gateway): ${endpoint} → ${fullUrl}`)
  return fullUrl
}

/**
 * Build API URL for database execution - ALWAYS uses API Gateway (never localhost)
 * This ensures database execution always goes through the remote Lambda sandboxes
 */
export function buildDatabaseExecutionApiUrl(endpoint: string): string {
  // ALWAYS use remote API for database execution (even in development)
  const baseUrl = API_CONFIG.REMOTE_API_BASE
  
  // Clean endpoint
  let cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint
  
  // Build URL with API version
  let fullUrl: string
  if (baseUrl.includes('/api/')) {
    // Base URL already includes /api/version path
    fullUrl = `${baseUrl}/${cleanEndpoint}`
  } else {
    // Base URL needs /api/version path
    fullUrl = `${baseUrl}/api/${API_CONFIG.API_VERSION}/${cleanEndpoint}`
  }
  
  console.log(`🗃️ DATABASE EXECUTION URL (ALWAYS API Gateway): ${endpoint} → ${fullUrl}`)
  return fullUrl
}

/**
 * Standard fetch with centralized configuration
 */
export async function apiRequest(endpoint: string, options: RequestInit = {}): Promise<Response> {
  const url = buildApiUrl(endpoint)
  
  const headers: Record<string, string> = {
    ...options.headers as Record<string, string>
  }
  
  // Only set Content-Type if body is not FormData
  // FormData requires browser to set Content-Type with boundary
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json'
  }
  
  // Add auth token if available
  if (typeof window !== 'undefined') {
    const token = localStorage.getItem('token')
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
  }
  
  console.log(`🚀 API Request: ${options.method || 'GET'} ${url}`)
  
  return fetch(url, {
    ...options,
    headers,
    credentials: 'omit'
  })
}

// Log configuration on startup
if (isDevelopment) {
  console.log('🔧 CENTRAL API CONFIG:', {
    remote: API_CONFIG.REMOTE_API_BASE,
    local: API_CONFIG.LOCAL_API_BASE,
    useLocal: API_CONFIG.USE_LOCAL_API,
    current: getApiBaseUrl()
  })
}

// Export for backward compatibility
export default {
  getApiBaseUrl,
  buildApiUrl,
  buildCodeExecutionApiUrl,
  buildDatabaseExecutionApiUrl,
  apiRequest,
  ...API_CONFIG
}
